import { useMemo } from 'react';
import { useTheme } from 'styled-components';
import { Spin } from 'antd';
import {
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import {
    FolderKanban,
    CheckCircle2,
    ListTodo,
    Users,
    Bug,
    Zap,
    Flag,
    Activity,
    Clock,
    Star,
    AlertTriangle,
    AlertCircle,
} from 'lucide-react';
import { useDashboardAnalytics } from '@hooks/analytics/useDashboardAnalytics';
import { useAnalyticsDashboardLayout } from '@hooks/analytics/useAnalyticsDashboardLayout';
import {
    AnalyticsDashRoot,
    AnalyticsDashHeader,
    AnalyticsDashTitleBlock,
    AnalyticsDashMainTitle,
    AnalyticsDashSubtitle,
    AnalyticsKpiGrid,
    AnalyticsKpiCard,
    AnalyticsKpiTopRow,
    AnalyticsKpiIconBox,
    AnalyticsKpiLabel,
    AnalyticsKpiValue,
    AnalyticsSection,
    AnalyticsSectionHeading,
    AnalyticsThreeCol,
    AnalyticsPanel,
    AnalyticsPanelTitle,
    AnalyticsPanelSub,
    AnalyticsChartBody,
    AnalyticsChartBodySm,
    AnalyticsProgressList,
    AnalyticsProgressRow,
    AnalyticsProgressName,
    AnalyticsProgressTrack,
    AnalyticsProgressFill,
    AnalyticsProgressPct,
    AnalyticsActivityList,
    AnalyticsActivityItem,
    AnalyticsActivityDot,
    AnalyticsActivityText,
    AnalyticsActivityTime,
    AnalyticsFooterGrid,
} from './analyticsDashboardStyles';

const KPI_ICONS = {
    folder: FolderKanban,
    check: CheckCircle2,
    list: ListTodo,
    users: Users,
    bug: Bug,
    zap: Zap,
    flag: Flag,
    gauge: Activity,
    clock: Clock,
    star: Star,
};

const KPI_PALETTE = [
    { bg: 'rgba(59, 130, 246, 0.12)', fg: '#3b82f6' },
    { bg: 'rgba(34, 197, 94, 0.12)', fg: '#16a34a' },
    { bg: 'rgba(168, 85, 247, 0.12)', fg: '#9333ea' },
    { bg: 'rgba(234, 179, 8, 0.15)', fg: '#ca8a04' },
    { bg: 'rgba(239, 68, 68, 0.12)', fg: '#dc2626' },
    { bg: 'rgba(14, 165, 233, 0.12)', fg: '#0284c7' },
    { bg: 'rgba(249, 115, 22, 0.12)', fg: '#ea580c' },
    { bg: 'rgba(99, 102, 241, 0.12)', fg: '#4f46e5' },
    { bg: 'rgba(20, 184, 166, 0.12)', fg: '#0d9488' },
    { bg: 'rgba(236, 72, 153, 0.12)', fg: '#db2777' },
];

function StatusGlyph({ status }) {
    if (status === 'ok') return <CheckCircle2 size={18} color="#16a34a" aria-hidden />;
    if (status === 'warn') return <AlertTriangle size={18} color="#ca8a04" aria-hidden />;
    return <AlertCircle size={18} color="#dc2626" aria-hidden />;
}

function DonutBlock({ title, sub, data, chartHeight = 220, emptyText = 'No data found' }) {
    const theme = useTheme();
    const isDark = theme.colors.background.default === '#141414';
    const total = useMemo(() => data.reduce((s, d) => s + (Number(d.value) || 0), 0), [data]);
    const legendColor = isDark ? 'rgba(255,255,255,0.75)' : '#4b5563';

    return (
        <>
            <AnalyticsPanelTitle>{title}</AnalyticsPanelTitle>
            <AnalyticsPanelSub>{sub || (total === 0 ? emptyText : '')}</AnalyticsPanelSub>
            <AnalyticsChartBody style={{ minHeight: chartHeight }}>
                <ResponsiveContainer width="100%" height={chartHeight}>
                    <PieChart>
                        <Pie
                            data={data}
                            cx="50%"
                            cy="48%"
                            innerRadius="52%"
                            outerRadius="72%"
                            paddingAngle={2}
                            dataKey="value"
                            nameKey="name"
                        >
                            {data.map((entry) => (
                                <Cell key={entry.name} fill={entry.color} stroke="transparent" />
                            ))}
                        </Pie>
                        <Tooltip
                            formatter={(v, _n, p) => {
                                const vv = Number(v) || 0;
                                const pct = total ? Math.round((vv / total) * 100) : 0;
                                return [`${vv} (${pct}%)`, p?.payload?.name];
                            }}
                            contentStyle={{
                                borderRadius: 8,
                                border: isDark ? '1px solid #333' : '1px solid #e5e7eb',
                                background: isDark ? '#262626' : '#fff',
                                color: isDark ? '#fff' : '#111',
                            }}
                        />
                        <Legend
                            layout="horizontal"
                            align="center"
                            verticalAlign="bottom"
                            wrapperStyle={{ paddingTop: 8, color: legendColor, fontSize: 12 }}
                            formatter={(value, entry) => {
                                const vv = entry?.payload?.value ?? 0;
                                const pct = total ? Math.round((vv / total) * 100) : 0;
                                return `${value}: ${vv} (${pct}%)`;
                            }}
                        />
                    </PieChart>
                </ResponsiveContainer>
            </AnalyticsChartBody>
        </>
    );
}

export function AnalyticsDashboard() {
    const theme = useTheme();
    const isDark = theme.colors.background.default === '#141414';
    const tickColor = isDark ? '#9ca3af' : '#6b7280';
    const gridColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
    const tooltipStyle = {
        borderRadius: 8,
        border: isDark ? '1px solid #333' : '1px solid #e5e7eb',
        background: isDark ? '#262626' : '#fff',
        color: isDark ? '#fff' : '#111',
    };

    const { kpiCards, recentNotifications, recentProjects, activityLogs, view, isLoading } = useDashboardAnalytics();
    const layout = useAnalyticsDashboardLayout();

    const projectStatusDonut = useMemo(
        () => (layout.projectStatusDonut ?? []).map((d) => ({ ...d, value: 0 })),
        [layout.projectStatusDonut],
    );
    const projectProgress = useMemo(
        () => (layout.projectProgress ?? []).map((p) => ({ ...p, percent: 0, status: 'bad' })),
        [layout.projectProgress],
    );
    const deliveryPerformance = useMemo(() => ({ onTimePercent: 0, delayPercent: 0, avgDelayDays: 0 }), []);
    const milestoneStatusBars = useMemo(
        () => (layout.milestoneStatusBars ?? []).map((m) => ({ ...m, value: 0 })),
        [layout.milestoneStatusBars],
    );
    const readinessMilestone = useMemo(
        () => (layout.readinessMilestone ?? []).map((m) => ({ ...m, value: 0 })),
        [layout.readinessMilestone],
    );
    const taskCompletionLine = useMemo(
        () => (layout.taskCompletionLine ?? []).map((w) => ({ ...w, completed: 0, total: 0 })),
        [layout.taskCompletionLine],
    );
    const sprintVelocity = useMemo(
        () => (layout.sprintVelocity ?? []).map((s) => ({ ...s, value: 0 })),
        [layout.sprintVelocity],
    );
    const taskStatusDonut = useMemo(
        () => (layout.taskStatusDonut ?? []).map((d) => ({ ...d, value: 0 })),
        [layout.taskStatusDonut],
    );
    const bugTrends = useMemo(
        () => (layout.bugTrends ?? []).map((d) => ({ ...d, reported: 0, resolved: 0 })),
        [layout.bugTrends],
    );
    const bugSeverityDonut = useMemo(
        () => (layout.bugSeverityDonut ?? []).map((d) => ({ ...d, value: 0 })),
        [layout.bugSeverityDonut],
    );
    const qaMetrics = useMemo(
        () => (layout.qaMetrics ?? []).map((q) => ({ ...q, value: '0' })),
        [layout.qaMetrics],
    );
    const teamWorkload = useMemo(
        () =>
            Array.from({ length: 5 }).map((_, i) => ({
                name: `Member ${i + 1}`,
                value: 0,
            })),
        [],
    );
    const topContributors = useMemo(
        () =>
            Array.from({ length: 4 }).map((_, i) => ({
                rank: i + 1,
                name: `Contributor ${i + 1}`,
                role: '—',
                score: 0,
            })),
        [],
    );
    const workModeDonut = useMemo(
        () => (layout.workModeDonut ?? []).map((d) => ({ ...d, value: 0 })),
        [layout.workModeDonut],
    );
    const workHoursLine = useMemo(
        () => (layout.workHoursLine ?? []).map((d) => ({ ...d, hours: 0 })),
        [layout.workHoursLine],
    );
    const timeByProject = useMemo(
        () =>
            Array.from({ length: 4 }).map((_, i) => ({
                name: `Project ${i + 1}`,
                hours: 0,
            })),
        [],
    );

    return (
        <AnalyticsDashRoot>
            <AnalyticsDashHeader>
                <AnalyticsDashTitleBlock>
                    <AnalyticsDashMainTitle>Analytics Dashboard</AnalyticsDashMainTitle>
                    <AnalyticsDashSubtitle>
                        {view === 'admin'
                            ? 'Admin'
                            : view === 'pm'
                            ? 'Project Manager'
                            : view === 'qa'
                            ? 'QA'
                            : view === 'client'
                            ? 'Client'
                            : 'Developer'}{' '}
                        overview (live metrics)
                    </AnalyticsDashSubtitle>
                </AnalyticsDashTitleBlock>
            </AnalyticsDashHeader>

            {isLoading ? (
                <div style={{ padding: '12px 0' }}>
                    <Spin />
                </div>
            ) : null}

            <AnalyticsKpiGrid>
                {kpiCards.map((k, i) => {
                    const Icon = KPI_ICONS[k.icon] || FolderKanban;
                    const pal = KPI_PALETTE[i % KPI_PALETTE.length];
                    return (
                        <AnalyticsKpiCard key={k.key}>
                            <AnalyticsKpiTopRow>
                                <AnalyticsKpiIconBox $bg={pal.bg} $fg={pal.fg}>
                                    <Icon size={20} aria-hidden />
                                </AnalyticsKpiIconBox>
                            </AnalyticsKpiTopRow>
                            <AnalyticsKpiLabel>{k.label}</AnalyticsKpiLabel>
                            <AnalyticsKpiValue>{k.value ?? 0}</AnalyticsKpiValue>
                        </AnalyticsKpiCard>
                    );
                })}
            </AnalyticsKpiGrid>

            {['admin', 'client', 'pm', 'qa'].includes(view) && (
                <AnalyticsSection>
                    <AnalyticsSectionHeading>Project Health Overview</AnalyticsSectionHeading>
                    <AnalyticsThreeCol>
                        <AnalyticsPanel>
                            <DonutBlock title="Project Status Distribution" sub="No data found" data={projectStatusDonut} />
                        </AnalyticsPanel>
                        <AnalyticsPanel style={{ gridColumn: 'span 2' }}>
                            <AnalyticsPanelTitle>Project Progress Tracker</AnalyticsPanelTitle>
                            <AnalyticsPanelSub>No data found</AnalyticsPanelSub>
                            <AnalyticsProgressList>
                                {projectProgress.map((p) => (
                                    <AnalyticsProgressRow key={p.name}>
                                        <AnalyticsProgressName title={p.name}>{p.name}</AnalyticsProgressName>
                                        <AnalyticsProgressTrack>
                                            <AnalyticsProgressFill $pct={p.percent} />
                                        </AnalyticsProgressTrack>
                                        <AnalyticsProgressPct>{p.percent}%</AnalyticsProgressPct>
                                        <StatusGlyph status={p.status} />
                                    </AnalyticsProgressRow>
                                ))}
                            </AnalyticsProgressList>
                        </AnalyticsPanel>
                    </AnalyticsThreeCol>
                </AnalyticsSection>
            )}

            {['admin', 'client', 'pm'].includes(view) && (
                <AnalyticsSection>
                    <AnalyticsSectionHeading>Milestone Performance</AnalyticsSectionHeading>
                    <AnalyticsThreeCol>
                        <AnalyticsPanel>
                            <AnalyticsPanelTitle>Milestone Status Breakdown</AnalyticsPanelTitle>
                            <AnalyticsPanelSub>No data found</AnalyticsPanelSub>
                            <AnalyticsChartBodySm>
                                <ResponsiveContainer width="100%" height={220}>
                                    <BarChart data={milestoneStatusBars} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
                                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: tickColor }} axisLine={false} tickLine={false} />
                                        <YAxis tick={{ fontSize: 11, fill: tickColor }} axisLine={false} tickLine={false} />
                                        <Tooltip contentStyle={tooltipStyle} />
                                        <Bar dataKey="value" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Count" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </AnalyticsChartBodySm>
                        </AnalyticsPanel>
                        <AnalyticsPanel>
                            <AnalyticsPanelTitle>Delivery Performance</AnalyticsPanelTitle>
                            <AnalyticsPanelSub>No data found</AnalyticsPanelSub>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
                                <div>On-Time Delivery: {deliveryPerformance.onTimePercent}%</div>
                                <div>Delays: {deliveryPerformance.delayPercent}%</div>
                                <div>Average Delay: {deliveryPerformance.avgDelayDays} days</div>
                            </div>
                        </AnalyticsPanel>
                        <AnalyticsPanel>
                            <AnalyticsPanelTitle>Readiness Milestone</AnalyticsPanelTitle>
                            <AnalyticsPanelSub>No data found</AnalyticsPanelSub>
                            <AnalyticsChartBodySm>
                                <ResponsiveContainer width="100%" height={220}>
                                    <BarChart data={readinessMilestone} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
                                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: tickColor }} axisLine={false} tickLine={false} />
                                        <YAxis tick={{ fontSize: 11, fill: tickColor }} axisLine={false} tickLine={false} />
                                        <Tooltip contentStyle={tooltipStyle} />
                                        <Bar dataKey="value" fill="#22c55e" radius={[4, 4, 0, 0]} name="Readiness" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </AnalyticsChartBodySm>
                        </AnalyticsPanel>
                    </AnalyticsThreeCol>
                </AnalyticsSection>
            )}

            {['admin', 'pm', 'general', 'qa'].includes(view) && (
                <AnalyticsSection>
                    <AnalyticsSectionHeading>Task &amp; Sprint Analytics</AnalyticsSectionHeading>
                    <AnalyticsThreeCol>
                        <AnalyticsPanel>
                            <AnalyticsPanelTitle>Task Completion Rate</AnalyticsPanelTitle>
                            <AnalyticsPanelSub>No data found</AnalyticsPanelSub>
                            <AnalyticsChartBodySm>
                                <ResponsiveContainer width="100%" height={220}>
                                    <LineChart data={taskCompletionLine} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: tickColor }} axisLine={false} tickLine={false} />
                                        <YAxis tick={{ fontSize: 11, fill: tickColor }} axisLine={false} tickLine={false} />
                                        <Tooltip contentStyle={tooltipStyle} />
                                        <Legend wrapperStyle={{ fontSize: 12, color: tickColor }} />
                                        <Line type="monotone" dataKey="completed" stroke="#22c55e" strokeWidth={2} dot={{ r: 3 }} name="Completed" />
                                        <Line type="monotone" dataKey="total" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} name="Total" />
                                    </LineChart>
                                </ResponsiveContainer>
                            </AnalyticsChartBodySm>
                        </AnalyticsPanel>
                        <AnalyticsPanel>
                            <AnalyticsPanelTitle>Sprint Velocity</AnalyticsPanelTitle>
                            <AnalyticsPanelSub>No data found</AnalyticsPanelSub>
                            <AnalyticsChartBodySm>
                                <ResponsiveContainer width="100%" height={220}>
                                    <BarChart data={sprintVelocity} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
                                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: tickColor }} axisLine={false} tickLine={false} />
                                        <YAxis tick={{ fontSize: 11, fill: tickColor }} axisLine={false} tickLine={false} />
                                        <Tooltip contentStyle={tooltipStyle} />
                                        <Bar dataKey="value" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Velocity" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </AnalyticsChartBodySm>
                        </AnalyticsPanel>
                        <AnalyticsPanel>
                            <DonutBlock title="Task Status Distribution" sub="No data found" data={taskStatusDonut} />
                        </AnalyticsPanel>
                    </AnalyticsThreeCol>
                </AnalyticsSection>
            )}

            {['admin', 'general', 'qa'].includes(view) && (
                <AnalyticsSection>
                    <AnalyticsSectionHeading>QA &amp; Bug Analytics</AnalyticsSectionHeading>
                    <AnalyticsThreeCol>
                        <AnalyticsPanel>
                            <AnalyticsPanelTitle>Bug Trends</AnalyticsPanelTitle>
                            <AnalyticsPanelSub>No data found</AnalyticsPanelSub>
                            <AnalyticsChartBodySm>
                                <ResponsiveContainer width="100%" height={220}>
                                    <LineChart data={bugTrends} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: tickColor }} axisLine={false} tickLine={false} />
                                        <YAxis tick={{ fontSize: 11, fill: tickColor }} axisLine={false} tickLine={false} />
                                        <Tooltip contentStyle={tooltipStyle} />
                                        <Legend wrapperStyle={{ fontSize: 12, color: tickColor }} />
                                        <Line type="monotone" dataKey="reported" stroke="#ef4444" strokeWidth={2} dot={{ r: 3 }} name="Reported" />
                                        <Line type="monotone" dataKey="resolved" stroke="#22c55e" strokeWidth={2} dot={{ r: 3 }} name="Resolved" />
                                    </LineChart>
                                </ResponsiveContainer>
                            </AnalyticsChartBodySm>
                        </AnalyticsPanel>
                        <AnalyticsPanel>
                            <DonutBlock title="Bug Severity Distribution" sub="No data found" data={bugSeverityDonut} />
                        </AnalyticsPanel>
                        <AnalyticsPanel>
                            <AnalyticsPanelTitle>QA Performance Metrics</AnalyticsPanelTitle>
                            <AnalyticsPanelSub>No data found</AnalyticsPanelSub>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12, marginTop: 8 }}>
                                {qaMetrics.map((q) => (
                                    <div key={q.key} style={{ padding: '14px 16px', borderRadius: 10, textAlign: 'center' }}>
                                        <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 6, opacity: 0.9 }}>{q.label}</div>
                                        <div style={{ fontSize: 18 }}>{q.value}</div>
                                    </div>
                                ))}
                            </div>
                        </AnalyticsPanel>
                    </AnalyticsThreeCol>
                </AnalyticsSection>
            )}

            {['admin', 'pm', 'general'].includes(view) && (
                <AnalyticsSection>
                    <AnalyticsSectionHeading>Team Productivity</AnalyticsSectionHeading>
                    <AnalyticsThreeCol>
                        <AnalyticsPanel>
                            <AnalyticsPanelTitle>Team Workload Distribution</AnalyticsPanelTitle>
                            <AnalyticsPanelSub>No data found</AnalyticsPanelSub>
                            <AnalyticsChartBodySm>
                                <ResponsiveContainer width="100%" height={220}>
                                    <BarChart data={teamWorkload} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
                                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: tickColor }} axisLine={false} tickLine={false} />
                                        <YAxis tick={{ fontSize: 11, fill: tickColor }} axisLine={false} tickLine={false} />
                                        <Tooltip contentStyle={tooltipStyle} />
                                        <Bar dataKey="value" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Load" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </AnalyticsChartBodySm>
                        </AnalyticsPanel>
                        <AnalyticsPanel>
                            <AnalyticsPanelTitle>Top Contributors</AnalyticsPanelTitle>
                            <AnalyticsPanelSub>No data found</AnalyticsPanelSub>
                            <AnalyticsActivityList>
                                {topContributors.map((c) => (
                                    <AnalyticsActivityItem key={c.rank}>
                                        <AnalyticsActivityDot $tone="yellow" />
                                        <div>
                                            <AnalyticsActivityText>{c.name}</AnalyticsActivityText>
                                            <AnalyticsActivityTime>{c.score}</AnalyticsActivityTime>
                                        </div>
                                    </AnalyticsActivityItem>
                                ))}
                            </AnalyticsActivityList>
                        </AnalyticsPanel>
                        <AnalyticsPanel>
                            <DonutBlock title="Work Mode Distribution" sub="No data found" data={workModeDonut} chartHeight={200} />
                        </AnalyticsPanel>
                    </AnalyticsThreeCol>
                </AnalyticsSection>
            )}

            <AnalyticsSection>
                <AnalyticsSectionHeading>Latest</AnalyticsSectionHeading>
                <AnalyticsFooterGrid>
                    <AnalyticsPanel>
                        <AnalyticsPanelTitle>Recent Projects</AnalyticsPanelTitle>
                        <AnalyticsPanelSub>Newest activity</AnalyticsPanelSub>
                        <AnalyticsActivityList>
                            {(recentProjects ?? []).length ? (
                                recentProjects.slice(0, 6).map((p, idx) => (
                                    <AnalyticsActivityItem key={p?.id ?? p?._id ?? `${idx}`}>
                                        <AnalyticsActivityDot $tone="blue" />
                                        <div>
                                            <AnalyticsActivityText>{p?.name ?? p?.title ?? 'Untitled'}</AnalyticsActivityText>
                                            <AnalyticsActivityTime>{p?.updatedAt ?? p?.createdAt ?? ''}</AnalyticsActivityTime>
                                        </div>
                                    </AnalyticsActivityItem>
                                ))
                            ) : (
                                <AnalyticsActivityItem>
                                    <AnalyticsActivityDot $tone="yellow" />
                                    <div>
                                        <AnalyticsActivityText>No projects yet</AnalyticsActivityText>
                                        <AnalyticsActivityTime>0</AnalyticsActivityTime>
                                    </div>
                                </AnalyticsActivityItem>
                            )}
                        </AnalyticsActivityList>
                    </AnalyticsPanel>
                    <AnalyticsPanel>
                        <AnalyticsPanelTitle>Recent Notifications</AnalyticsPanelTitle>
                        <AnalyticsPanelSub>Latest messages</AnalyticsPanelSub>
                        <AnalyticsActivityList>
                            {(recentNotifications ?? []).length ? (
                                recentNotifications.slice(0, 6).map((n, idx) => (
                                    <AnalyticsActivityItem key={n?.id ?? n?._id ?? `${idx}`}>
                                        <AnalyticsActivityDot $tone="green" />
                                        <div>
                                            <AnalyticsActivityText>{n?.title ?? n?.message ?? n?.text ?? 'Notification'}</AnalyticsActivityText>
                                            <AnalyticsActivityTime>{n?.createdAt ?? n?.time ?? ''}</AnalyticsActivityTime>
                                        </div>
                                    </AnalyticsActivityItem>
                                ))
                            ) : (
                                <AnalyticsActivityItem>
                                    <AnalyticsActivityDot $tone="yellow" />
                                    <div>
                                        <AnalyticsActivityText>No notifications</AnalyticsActivityText>
                                        <AnalyticsActivityTime>0</AnalyticsActivityTime>
                                    </div>
                                </AnalyticsActivityItem>
                            )}
                        </AnalyticsActivityList>
                    </AnalyticsPanel>
                    {['admin', 'pm'].includes(view) && (
                        <AnalyticsPanel>
                            <AnalyticsPanelTitle>Activity Logs</AnalyticsPanelTitle>
                            <AnalyticsPanelSub>Latest events</AnalyticsPanelSub>
                            <AnalyticsActivityList>
                                {(activityLogs ?? []).length ? (
                                    activityLogs.slice(0, 6).map((a, idx) => (
                                        <AnalyticsActivityItem key={a?.id ?? a?._id ?? `${idx}`}>
                                            <AnalyticsActivityDot $tone="blue" />
                                            <div>
                                                <AnalyticsActivityText>{a?.text ?? a?.message ?? a?.title ?? 'Activity'}</AnalyticsActivityText>
                                                <AnalyticsActivityTime>{a?.createdAt ?? a?.time ?? ''}</AnalyticsActivityTime>
                                            </div>
                                        </AnalyticsActivityItem>
                                    ))
                                ) : (
                                    <AnalyticsActivityItem>
                                        <AnalyticsActivityDot $tone="yellow" />
                                        <div>
                                            <AnalyticsActivityText>No activity</AnalyticsActivityText>
                                            <AnalyticsActivityTime>0</AnalyticsActivityTime>
                                        </div>
                                    </AnalyticsActivityItem>
                                )}
                            </AnalyticsActivityList>
                        </AnalyticsPanel>
                    )}
                </AnalyticsFooterGrid>
            </AnalyticsSection>

            {['admin', 'pm'].includes(view) && (
                <AnalyticsSection>
                    <AnalyticsSectionHeading>Overview</AnalyticsSectionHeading>
                    <AnalyticsFooterGrid>
                        <AnalyticsPanel>
                            <AnalyticsPanelTitle>Average Work Hours per Day</AnalyticsPanelTitle>
                            <AnalyticsPanelSub>No data found</AnalyticsPanelSub>
                            <AnalyticsChartBodySm>
                                <ResponsiveContainer width="100%" height={220}>
                                    <LineChart data={workHoursLine} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: tickColor }} axisLine={false} tickLine={false} />
                                        <YAxis tick={{ fontSize: 11, fill: tickColor }} axisLine={false} tickLine={false} />
                                        <Tooltip contentStyle={tooltipStyle} />
                                        <Line type="monotone" dataKey="hours" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} name="Hours" />
                                    </LineChart>
                                </ResponsiveContainer>
                            </AnalyticsChartBodySm>
                        </AnalyticsPanel>
                        <AnalyticsPanel>
                            <AnalyticsPanelTitle>Time Spent by Project</AnalyticsPanelTitle>
                            <AnalyticsPanelSub>No data found</AnalyticsPanelSub>
                            <AnalyticsChartBodySm>
                                <ResponsiveContainer width="100%" height={220}>
                                    <BarChart data={timeByProject} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
                                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: tickColor }} axisLine={false} tickLine={false} />
                                        <YAxis tick={{ fontSize: 11, fill: tickColor }} axisLine={false} tickLine={false} />
                                        <Tooltip contentStyle={tooltipStyle} />
                                        <Bar dataKey="hours" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Hours" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </AnalyticsChartBodySm>
                        </AnalyticsPanel>
                        <AnalyticsPanel>
                            <AnalyticsPanelTitle>Recent Activity</AnalyticsPanelTitle>
                            <AnalyticsPanelSub>No data found</AnalyticsPanelSub>
                            <AnalyticsActivityList>
                                <AnalyticsActivityItem>
                                    <AnalyticsActivityDot $tone="yellow" />
                                    <div>
                                        <AnalyticsActivityText>No data found</AnalyticsActivityText>
                                        <AnalyticsActivityTime>0</AnalyticsActivityTime>
                                    </div>
                                </AnalyticsActivityItem>
                            </AnalyticsActivityList>
                        </AnalyticsPanel>
                    </AnalyticsFooterGrid>
                </AnalyticsSection>
            )}
        </AnalyticsDashRoot>
    );
}
